/**
 * UserProfile Component
 * Allows users to view and edit their profile
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ChangePasswordModal from './ChangePasswordModal';
import { formatServerDate, formatServerDateTime } from '../utils/dateFormat';
import './UserProfile.css';

function UserProfile({ onBack }) {
  const [profile, setProfile] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    jobTitle: '',
    facilityId: ''
  });
  const [themePreference, setThemePreference] = useState(
    () => localStorage.getItem('theme') || 'light'
  );
  const [professionRole, setProfessionRole] = useState('sonographer');
  const [technologistList, setTechnologistList] = useState([]);
  const [defaultTechnologistId, setDefaultTechnologistId] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    loadProfile();
    loadFacilities();
    loadTechnologists();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProfile(response.data.profile);
      setFormData({
        fullName: response.data.profile.fullName || '',
        email: response.data.profile.email || '',
        jobTitle: response.data.profile.jobTitle || '',
        facilityId: response.data.profile.facilityId || ''
      });
      if (response.data.profile.settings?.theme_preference) {
        setThemePreference(response.data.profile.settings.theme_preference);
      }
      if (response.data.profile.settings?.profession_role) {
        setProfessionRole(response.data.profile.settings.profession_role);
      }
      setDefaultTechnologistId(response.data.profile.settings?.default_technologist_id || '');
    } catch (error) {
      console.error('Error loading profile:', error);
      alert('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = async (newTheme) => {
    setThemePreference(newTheme);
    localStorage.setItem('theme', newTheme);
    window.dispatchEvent(new Event('theme-change'));
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile/settings`,
        { themePreference: newTheme },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save theme preference:', error);
    }
  };

  const handleProfessionRoleChange = async (newRole) => {
    setProfessionRole(newRole);
    localStorage.setItem('professionRole', newRole);
    window.dispatchEvent(new Event('profession-role-change'));
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile/settings`,
        { professionRole: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Technologist list is scoped by profession — refresh it, and take the
      // selection the server re-linked for the new profession
      setDefaultTechnologistId(response.data.settings?.default_technologist_id || '');
      loadTechnologists();
    } catch (error) {
      console.error('Failed to save default view preference:', error);
    }
  };

  const handleDefaultTechnologistChange = async (newId) => {
    setDefaultTechnologistId(newId);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile/settings`,
        { defaultTechnologistId: newId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Failed to save default technologist:', error);
    }
  };

  const loadFacilities = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/profile/facilities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFacilities(response.data.facilities || []);
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  };

  const loadTechnologists = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/profile/technologists`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTechnologistList(response.data.technologistList || []);
    } catch (error) {
      console.error('Error loading technologists:', error);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/profile`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Profile updated successfully!');
      setIsEditing(false);
      loadProfile();
      loadTechnologists(); // Facility may have changed — refresh the scoped technologist list


      // Update localStorage userInfo
      const storedUser = JSON.parse(localStorage.getItem('userInfo'));
      const updatedUser = { ...storedUser, fullName: formData.fullName, email: formData.email, jobTitle: formData.jobTitle };
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      fullName: profile.fullName || '',
      email: profile.email || '',
      jobTitle: profile.jobTitle || '',
      facilityId: profile.facilityId || ''
    });
    setIsEditing(false);
  };

  if (!profile) {
    return <div className="profile-loading">Loading profile...</div>;
  }

  return (
    <div className="user-profile">
      {showPasswordModal && (
        <ChangePasswordModal
          isForced={false}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={() => {
            setShowPasswordModal(false);
            alert('Password changed successfully!');
          }}
        />
      )}

      <div className="profile-header">
        <h1>My Profile</h1>
        <button onClick={onBack} className="btn-back">Back to Worksheets</button>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-section">
            <h2>Account Information</h2>
            
            {!isEditing ? (
              // View Mode
              <div className="profile-info">
                <div className="info-row">
                  <label>Username:</label>
                  <span>{profile.username}</span>
                </div>
                <div className="info-row">
                  <label>Full Name:</label>
                  <span>{profile.fullName || '-'}</span>
                </div>
                <div className="info-row">
                  <label>Email:</label>
                  <span>{profile.email || '-'}</span>
                </div>
                <div className="info-row">
                  <label>Job Title:</label>
                  <span>{profile.jobTitle || '-'}</span>
                </div>
                <div className="info-row">
                  <label>Role:</label>
                  <span className={`role-badge role-${profile.role}`}>
                    {profile.role === 'admin' ? 'Administrator' : 'User'}
                  </span>
                </div>
                <div className="info-row">
                  <label>Facility:</label>
                  <span>{profile.facilityName || 'Not assigned'}</span>
                </div>
                <div className="info-row">
                  <label>Account Created:</label>
                  <span>{formatServerDate(profile.createdAt)}</span>
                </div>
                <div className="info-row">
                  <label>Last Login:</label>
                  <span>{profile.lastLogin ? formatServerDateTime(profile.lastLogin) : 'Never'}</span>
                </div>

                <div className="profile-actions">
                  <button onClick={() => setIsEditing(true)} className="btn-primary">
                    Edit Profile
                  </button>
                  <button onClick={() => setShowPasswordModal(true)} className="btn-secondary">
                    Change Password
                  </button>
                </div>
              </div>
            ) : (
              // Edit Mode
              <form onSubmit={handleSave} className="profile-form">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    value={profile.username}
                    disabled
                    className="disabled-input"
                  />
                  <small>Username cannot be changed</small>
                </div>

                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Job Title</label>
                  <input
                    type="text"
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({...formData, jobTitle: e.target.value})}
                  />
                </div>

                <div className="form-group">
                  <label>Facility</label>
                  <select
                    value={formData.facilityId}
                    onChange={(e) => setFormData({...formData, facilityId: e.target.value})}
                  >
                    <option value="">None (No Facility)</option>
                    {facilities.map(f => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                  <small>Select your facility or choose "None" for traveling technologists</small>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-primary" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button type="button" onClick={handleCancel} className="btn-secondary" disabled={loading}>
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="profile-card">
          <div className="profile-section">
            <h2>Settings</h2>
            <div className="info-row">
              <label>Default Technologist Name:</label>
              {technologistList.length > 0 ? (
                <select
                  value={defaultTechnologistId}
                  onChange={(e) => handleDefaultTechnologistChange(e.target.value)}
                >
                  <option value="">Not set</option>
                  {technologistList.map(t => (
                    <option key={t.id} value={t.id}>{t.full_name}</option>
                  ))}
                </select>
              ) : (
                <small>
                  {profile.facilityId
                    ? "No technologists found for your facility and role yet. Ask an administrator to add one in Technologist Management."
                    : "No facility assigned, so there are no matching technologists to choose from. Set a facility above, then technologists for that facility will appear here."}
                </small>
              )}
            </div>
            <div className="info-row">
              <label>Theme:</label>
              <select
                value={themePreference}
                onChange={(e) => handleThemeChange(e.target.value)}
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </div>
            <div className="info-row">
              <label>Default View:</label>
              <select
                value={professionRole}
                onChange={(e) => handleProfessionRoleChange(e.target.value)}
              >
                <option value="sonographer">Sonographer Worksheets</option>
                <option value="nurse">Nurse — Lung Screening</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfile;
